"use client";

import { c as _c } from "react/compiler-runtime";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { LuAtSign, LuColumns, LuHeading1, LuHeading2, LuHeading3, LuImage, LuLayoutGrid, LuLayoutPanelLeft, LuLink, LuList, LuListOrdered, LuMinus, LuMousePointerClick, LuSparkles, LuSquare, LuTextQuote, LuType, LuWallpaper } from "react-icons/lu";
import { cx } from "@/lib/tailwind-utils";
export interface SlashCommandItem {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  section: string;
  /** Extra words the search matches, e.g. "hero" for a background image. */
  keywords?: string[];
  /** Called when the command is selected. */
  action: () => void;
}
interface SlashCommandListRef {
  onKeyDown: (props: {
    event: KeyboardEvent;
  }) => boolean;
}
interface SlashCommandListProps {
  items: SlashCommandItem[];
  command: (item: SlashCommandItem) => void;
}
export const defaultSlashCommands = ({
  onAI
}: {
  onAI?: () => void;
}): Omit<SlashCommandItem, "action">[] => [{
  id: "variable",
  label: "Variable",
  description: "Insert @ variable",
  icon: <LuAtSign size={16} />,
  section: "Quick Actions"
}, ...(onAI ? [{
  id: "ai",
  label: "AI",
  description: "Generate with AI",
  icon: <LuSparkles size={16} />,
  section: "Quick Actions"
}] : []), {
  id: "text",
  label: "Text",
  description: "Start with plain text",
  icon: <LuType size={16} />,
  section: "Text"
}, {
  id: "heading1",
  label: "Heading 1",
  description: "Large section heading",
  icon: <LuHeading1 size={16} />,
  section: "Text"
}, {
  id: "heading2",
  label: "Heading 2",
  description: "Medium section heading",
  icon: <LuHeading2 size={16} />,
  section: "Text"
}, {
  id: "heading3",
  label: "Heading 3",
  description: "Small section heading",
  icon: <LuHeading3 size={16} />,
  section: "Text"
}, {
  id: "bulletList",
  label: "Bullet List",
  description: "Unordered list",
  icon: <LuList size={16} />,
  section: "Text"
}, {
  id: "orderedList",
  label: "Numbered List",
  description: "Ordered list",
  icon: <LuListOrdered size={16} />,
  section: "Text"
}, {
  id: "quote",
  label: "Quote",
  description: "Block quote",
  icon: <LuTextQuote size={16} />,
  section: "Text"
}, {
  id: "image",
  label: "Image",
  description: "Upload an image",
  icon: <LuImage size={16} />,
  section: "Media",
  keywords: ["photo", "picture", "banner"]
}, {
  id: "imageLink",
  label: "Image from link",
  description: "Use an image that's already online",
  icon: <LuLink size={16} />,
  section: "Media",
  keywords: ["url", "link", "hosted", "web"]
}, {
  id: "backgroundImage",
  label: "Text on image",
  description: "Background image with text over it",
  icon: <LuWallpaper size={16} />,
  section: "Media",
  keywords: ["hero", "banner", "background", "cover"]
}, {
  id: "imageText",
  label: "Image + text",
  description: "Image beside text",
  icon: <LuLayoutPanelLeft size={16} />,
  section: "Media",
  keywords: ["side", "columns"]
}, {
  id: "button",
  label: "Button",
  description: "Clickable button link",
  icon: <LuMousePointerClick size={16} />,
  section: "Layout",
  keywords: ["cta", "link"]
}, {
  id: "section",
  label: "Section",
  description: "Coloured block with padding",
  icon: <LuSquare size={16} />,
  section: "Layout",
  keywords: ["block", "background", "band", "container"]
}, {
  id: "twoColumns",
  label: "2 columns",
  description: "Two blocks side by side",
  icon: <LuColumns size={16} />,
  section: "Layout",
  keywords: ["two", "columns", "grid"]
}, {
  id: "threeColumns",
  label: "3 columns",
  description: "Three blocks side by side",
  icon: <LuLayoutGrid size={16} />,
  section: "Layout",
  keywords: ["three", "columns", "grid"]
}, {
  id: "divider",
  label: "Divider",
  description: "Horizontal line",
  icon: <LuMinus size={16} />,
  section: "Layout",
  keywords: ["line", "separator", "rule", "hr"]
}];
const SlashCommandList = forwardRef<SlashCommandListRef, SlashCommandListProps>((t0, ref) => {
  const $ = _c(22);
  const {
    items,
    command
  } = t0;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setSelectedIndex(0);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== items) {
    t2 = [items];
    $[1] = items;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      selectedRef.current?.scrollIntoView({
        block: "nearest"
      });
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== selectedIndex) {
    t4 = [selectedIndex];
    $[4] = selectedIndex;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== command || $[7] !== items || $[8] !== selectedIndex) {
    t5 = () => ({
      onKeyDown: t6 => {
        const {
          event
        } = t6;
        if (event.key === "ArrowUp") {
          setSelectedIndex((selectedIndex + items.length - 1) % items.length);
          return true;
        }
        if (event.key === "ArrowDown") {
          setSelectedIndex((selectedIndex + 1) % items.length);
          return true;
        }
        if (event.key === "Enter") {
          const item = items[selectedIndex];
          if (item) {
            command(item);
          }
          return true;
        }
        return false;
      }
    });
    $[6] = command;
    $[7] = items;
    $[8] = selectedIndex;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useImperativeHandle(ref, t5);
  if (items.length === 0) {
    return null;
  }
  let t6;
  let t7;
  let t8;
  if ($[10] !== command || $[11] !== items || $[12] !== selectedIndex) {
    const sections = [];
    let currentSection = null;
    items.forEach((item_0, index) => {
      if (!currentSection || currentSection.label !== item_0.section) {
        currentSection = {
          label: item_0.section,
          items: []
        };
        sections.push(currentSection);
      }
      currentSection.items.push({
        item: item_0,
        globalIndex: index
      });
    });
    t8 = "max-h-72 w-64 overflow-y-auto rounded-lg border bg-background/95 shadow-lg backdrop-blur-sm";
    t6 = "p-1.5";
    t7 = sections.map(section => <div key={section.label}><div className="px-2 pb-0.5 pt-1.5 text-tiny font-medium uppercase tracking-wider text-foreground-tertiary">{section.label}</div>{section.items.map(t9 => {
        const {
          item: item_1,
          globalIndex
        } = t9;
        return <button type="button" key={item_1.id} ref={globalIndex === selectedIndex ? selectedRef : undefined} onClick={() => command(item_1)} className={cx("flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors duration-75", globalIndex === selectedIndex ? "bg-primary/10 text-foreground" : "text-foreground hover:bg-hover")}><span className="flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-background text-foreground-secondary">{item_1.icon}</span><div className="min-w-0"><p className="text-caption-strong">{item_1.label}</p><p className="truncate text-caption text-foreground-secondary">{item_1.description}</p></div></button>;
      })}</div>);
    $[10] = command;
    $[11] = items;
    $[12] = selectedIndex;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t6 || $[17] !== t7) {
    t9 = <div className={t6}>{t7}</div>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = <div className={t8}>{t9}</div>;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
});
SlashCommandList.displayName = "SlashCommandList";
export default SlashCommandList;